import React, { useState, useEffect, useCallback } from "react";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import { makeStyles } from "@material-ui/core/styles";
import CountryBadge from "./CountryBadge.js";

const useStyles = makeStyles((theme) => ({
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 120,
  },
  checklistRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  checklistIcon: {
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  checklistLabel: {
    flex: 1,
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  checklistValue: {
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
  checklistValueNode: {
    marginLeft: theme.spacing(1),
  },
  editButton: {
    marginLeft: theme.spacing(1),
    minWidth: 0,
    padding: "2px 8px",
    fontSize: "0.7rem",
  },
  editField: {
    marginTop: theme.spacing(0.5),
    width: "100%",
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
  },
  statusChip: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 6,
    fontWeight: 700,
    fontSize: "0.72rem",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    marginLeft: theme.spacing(1),
  },
  overrideBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    background: "rgba(237, 108, 2, 0.07)",
    border: "1px solid #ed6c02",
    borderRadius: 6,
  },
  progress: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
}));

// Fields that can be edited inline from this modal.
const EDITABLE_FIELDS = [
  { key: "name", label: "Full name", type: "text" },
  { key: "nickname", label: "Nickname", type: "text" },
  { key: "dateOfBirth", label: "Date of birth", type: "text", hint: "YYYY-MM-DD" },
  { key: "country", label: "Country", type: "text", hint: "ISO code e.g. GB" },
  { key: "steamId64", label: "Steam ID", type: "text" },
  { key: "steamProfileUrl", label: "Steam profile URL", type: "text", hint: "https://..." },
  { key: "faceitProfileUrl", label: "FaceIT profile URL", type: "text", hint: "https://..." },
  { key: "phoneNumber", label: "Phone number", type: "text", hint: "+44… (international)" },
  { key: "gender", label: "Gender", type: "select", options: ["male", "female"] },
  { key: "termsAccepted", label: "Terms accepted", type: "checkbox" },
];

function ChecklistItem({ label, done, value, valueNode, onEdit }) {
  const classes = useStyles();
  const displayValue =
    value === null || value === undefined
      ? "—"
      : typeof value === "boolean"
      ? value
        ? "Yes"
        : "No"
      : String(value);

  return (
    <div className={classes.checklistRow}>
      {done ? (
        <CheckCircleIcon
          className={classes.checklistIcon}
          style={{ color: "#2e7d32", fontSize: 18 }}
        />
      ) : (
        <CancelIcon
          className={classes.checklistIcon}
          style={{ color: "#d32f2f", fontSize: 18 }}
        />
      )}
      <Typography className={classes.checklistLabel}>{label}</Typography>
      {valueNode ? (
        <div className={classes.checklistValueNode}>{valueNode}</div>
      ) : (
        <Tooltip title={displayValue} placement="top" arrow>
          <Typography className={classes.checklistValue}>{displayValue}</Typography>
        </Tooltip>
      )}
      {onEdit && (
        <Button
          className={classes.editButton}
          size="small"
          variant="outlined"
          color="primary"
          onClick={onEdit}
        >
          Edit
        </Button>
      )}
    </div>
  );
}

function WarningList({ readiness }) {
  const warnings = readiness?.warningItems || [];
  if (!warnings.length) return null;

  return (
    <Alert severity="warning" style={{ marginBottom: 12 }}>
      {warnings.map((warning) => (
        <div key={warning}>{warning}</div>
      ))}
    </Alert>
  );
}

export default function ProfileReadinessReviewDialog({
  openState,
  userContext,
  adminUserId,
  edgeBasePath,
  onClose,
  onComplete,
}) {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [readinessData, setReadinessData] = useState(null);

  const [editingField, setEditingField] = useState(null);
  const [fieldDrafts, setFieldDrafts] = useState({});

  const [forceOverride, setForceOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const row = userContext?.row || null;

  // ---- Load readiness data -----
  const loadReadiness = useCallback(async () => {
    if (!row?.userId || !adminUserId) return;

    setLoading(true);
    setError("");
    setReadinessData(null);

    try {
      const res = await fetch(
        `${edgeBasePath}/api/v7/admin/users/${row.userId}/readiness-review/adpass/${adminUserId}`,
        { credentials: "include" }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        throw new Error(data.error || data.err || `Request failed (${res.status})`);
      }

      setReadinessData(data);
    } catch (err) {
      setError(err.message || "Failed to load readiness data");
    } finally {
      setLoading(false);
    }
  }, [row?.userId, adminUserId, edgeBasePath]);

  useEffect(() => {
    if (openState) {
      setFieldDrafts({});
      setEditingField(null);
      setForceOverride(false);
      setSubmitting(false);
      setSubmitError("");
      setSubmitSuccess(false);
      loadReadiness();
    }
  }, [openState, loadReadiness]);

  // ---- Submit -----
  const handleSubmit = async () => {
    if (!row?.userId || !adminUserId) return;

    setSubmitting(true);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      const res = await fetch(
        `${edgeBasePath}/api/v7/admin/users/${row.userId}/move-to-pending-review/adpass/${adminUserId}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            forceOverride,
            fieldUpdates: Object.keys(fieldDrafts).length > 0 ? fieldDrafts : undefined,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        throw new Error(data.error || data.err || `Request failed (${res.status})`);
      }

      setSubmitSuccess(true);
      onComplete();
      setTimeout(() => {
        onClose();
      }, 1400);
    } catch (err) {
      setSubmitError(err.message || "Failed to move user to pending_review");
    } finally {
      setSubmitting(false);
    }
  };

  // Guard render before row is set
  if (!row) return null;

  const { readiness, fields, accountStatus, profileTrustFactor } = readinessData || {};
  const completed = readiness?.completed ?? 0;
  const total = readiness?.total ?? 0;
  const allDone = readiness?.allDone ?? false;
  const checks = readiness?.checks ?? {};

  const statusColor =
    accountStatus === "pending_review"
      ? "#d97706"
      : accountStatus === "verified"
      ? "#2e7d32"
      : accountStatus === "banned"
      ? "#1f2937"
      : "#64748b";

  const canMove =
    !submitting &&
    !submitSuccess &&
    readinessData !== null &&
    accountStatus !== "pending_review" &&
    accountStatus !== "verified" &&
    accountStatus !== "banned" &&
    accountStatus !== "noteligible" &&
    (allDone || forceOverride);

  return (
    <Dialog
      open={openState}
      onClose={submitting ? undefined : onClose}
      aria-labelledby="readiness-review-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="readiness-review-dialog-title">
        Onboarding checklist — {row.nickname || row.email}
        <span
          className={classes.statusChip}
          style={{ backgroundColor: statusColor, color: "#fff" }}
        >
          {accountStatus || "pending"}
        </span>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <div className={classes.loadingWrapper}>
            <CircularProgress size={32} />
          </div>
        )}

        {error && (
          <Alert severity="error" style={{ marginBottom: 12 }}>
            {error}
          </Alert>
        )}

        {!loading && readinessData && (
          <>
            {/* Progress summary */}
            <div className={classes.progress}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {completed} / {total} items complete
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                Trust factor: {(profileTrustFactor?.profileTrustFactor ?? row?.profileTrustFactor) != null ? Number(profileTrustFactor?.profileTrustFactor ?? row?.profileTrustFactor).toFixed(2) : "—"}/10
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                Tier: {profileTrustFactor?.completedOnboardingTier ?? row?.completedOnboardingTier ?? 0}
              </Typography>
              {allDone && (
                <Typography variant="body2" style={{ color: "#2e7d32", fontWeight: 600 }}>
                  Profile is ready.
                </Typography>
              )}
            </div>

            <WarningList readiness={readiness} />

            {/* Checklist */}
            <Typography className={classes.sectionTitle}>Readiness checklist</Typography>

            {EDITABLE_FIELDS.map(({ key, label, type, hint, options }) => {
              const check = checks[key];
              if (!check) return null;

              const currentValue =
                key in fieldDrafts ? fieldDrafts[key] : fields?.[key];
              const isEditing = editingField === key;

              return (
                <div key={key}>
                  <ChecklistItem
                    label={check.label || label}
                    done={check.done}
                    value={currentValue}
                    onEdit={
                      isEditing
                        ? null
                        : () => {
                            setEditingField(key);
                            if (!(key in fieldDrafts)) {
                              setFieldDrafts((prev) => ({
                                ...prev,
                                [key]:
                                  currentValue !== null && currentValue !== undefined
                                    ? type === "checkbox"
                                      ? Boolean(currentValue)
                                      : String(currentValue)
                                    : type === "checkbox"
                                    ? false
                                    : "",
                              }));
                            }
                          }
                    }
                  />

                  {isEditing && (
                    <div style={{ padding: "4px 0 8px 26px" }}>
                      {type === "checkbox" ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(fieldDrafts[key])}
                              onChange={(e) =>
                                setFieldDrafts((prev) => ({
                                  ...prev,
                                  [key]: e.target.checked,
                                }))
                              }
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" style={{ fontSize: "0.82rem" }}>
                              {label}
                            </Typography>
                          }
                        />
                      ) : type === "select" ? (
                        <Select
                          className={classes.editField}
                          variant="outlined"
                          value={fieldDrafts[key] || ""}
                          onChange={(e) =>
                            setFieldDrafts((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          inputProps={{ style: { padding: "8px 12px", fontSize: "0.85rem" } }}
                        >
                          {(options || []).map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <TextField
                          className={classes.editField}
                          variant="outlined"
                          size="small"
                          value={fieldDrafts[key] || ""}
                          placeholder={hint || ""}
                          onChange={(e) =>
                            setFieldDrafts((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          inputProps={{ style: { fontSize: "0.85rem" } }}
                        />
                      )}
                      <Button
                        size="small"
                        onClick={() => setEditingField(null)}
                        style={{ marginTop: 4, fontSize: "0.72rem" }}
                      >
                        Done
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Steam-only emailVerified check (for Steam sign-in users) */}
            {checks.emailVerified && (
              <>
                <Divider style={{ margin: "8px 0" }} />
                <ChecklistItem
                  label={checks.emailVerified.label}
                  done={checks.emailVerified.done}
                  value={fields?.emailVerifiedAt ? "Yes" : "No"}
                />
              </>
            )}

            {fields?.effectiveCountry && (
              <>
                <Divider style={{ margin: "8px 0" }} />
                <ChecklistItem
                  label="Effective country"
                  done
                  value={`${fields.effectiveCountryDisplayName || fields.effectiveCountry}${fields.countrySource === "geoip" ? " (GeoIP detected)" : ""}`}
                  valueNode={
                    <CountryBadge
                      code={fields.effectiveCountry}
                      displayName={fields.effectiveCountryDisplayName}
                      flagImageUrl={fields.effectiveCountryFlagImageUrl}
                      sourceLabel={fields.countrySource === "geoip" ? "GeoIP" : ""}
                    />
                  }
                />
              </>
            )}
            {fields?.geoDetectedCountry &&
              fields.geoDetectedCountry !== fields.country &&
              fields.countrySource !== "geoip" && (
                <ChecklistItem
                  label="GeoIP country"
                  done
                  value={fields.geoDetectedCountryDisplayName || fields.geoDetectedCountry}
                  valueNode={
                    <CountryBadge
                      code={fields.geoDetectedCountry}
                      displayName={fields.geoDetectedCountryDisplayName}
                      flagImageUrl={fields.geoDetectedCountryFlagImageUrl}
                      sourceLabel="GeoIP"
                    />
                  }
                />
              )}

            {/* Override for incomplete profiles */}
            {!allDone && (
              <div className={classes.overrideBox}>
                <Typography variant="body2" style={{ color: "#ed6c02", fontWeight: 600, marginBottom: 6 }}>
                  Profile is not fully complete ({total - completed} item
                  {total - completed !== 1 ? "s" : ""} missing)
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={forceOverride}
                      onChange={(e) => setForceOverride(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                      I have manually reviewed this profile and authorise moving it to pending
                      review despite the missing items
                    </Typography>
                  }
                />
              </div>
            )}

            {submitError && (
              <Alert severity="error" style={{ marginTop: 12 }}>
                {submitError}
              </Alert>
            )}

            {submitSuccess && (
              <Alert severity="success" style={{ marginTop: 12 }}>
                User moved to pending_review successfully.
              </Alert>
            )}

            {accountStatus === "pending_review" && (
              <Alert severity="info" style={{ marginTop: 12 }}>
                This user is already in <strong>pending_review</strong>.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" disabled={submitting}>
          Close
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!canMove}
        >
          {submitting ? "Moving…" : "Move to pending_review"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
